/**
 * Admin Configuration Service
 * Manages system-wide configuration settings stored in Firestore
 * Accessible only by admin users
 */

import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';

// Announcement banner with individual targeting
export interface AnnouncementBanner {
  message: string;
  targetAudience: 'all' | 'free' | 'pro' | 'premium';
}

export interface SystemConfig {
  // AI Credit Limits (used by aiCreditsService)
  aiCredits: {
    freeMonthlyLimit: number;
    proMonthlyLimit: number;
  };

  // Storage Limits (in MB)
  storage: {
    freeTotalMB: number;
    proTotalMB: number;
    premiumTotalMB: number;
    freePhotoSizeMB: number;
    proPhotoSizeMB: number;
    premiumPhotoSizeMB: number;
    freeDocumentSizeMB: number;
    proDocumentSizeMB: number;
    premiumDocumentSizeMB: number;
  };

  // Item Count Limits
  itemLimits: {
    free: {
      maxAddresses: number;
      maxVehicles: number;
      maxHealthcare: number;
      maxInsurance: number;
      maxFinancial: number;
    };
    pro: {
      maxAddresses: number; // 999999 = unlimited
      maxVehicles: number;
      maxHealthcare: number;
      maxInsurance: number;
      maxFinancial: number;
    };
    premium: {
      maxAddresses: number;
      maxVehicles: number;
      maxHealthcare: number;
      maxInsurance: number;
      maxFinancial: number;
    };
  };

  // Family Member Limits
  familyLimits: {
    free: {
      maxMembers: number;
      maxCoPilots: number;
      maxPassengers: number;
    };
    pro: {
      maxMembers: number; // 999999 = unlimited
      maxCoPilots: number;
      maxPassengers: number;
    };
    premium: {
      maxMembers: number;
      maxCoPilots: number;
      maxPassengers: number;
    };
  };

  // Attachment Limits (combined photos + docs per item)
  attachmentLimits: {
    free: {
      maxAttachmentsPerItem: number;
    };
    pro: {
      maxAttachmentsPerItem: number;
    };
    premium: {
      maxAttachmentsPerItem: number;
    };
  };

  // Share Bundle Settings
  shareBundle: {
    expiryDays: number;
  };

  // Feature Flags (Global)
  features: {
    enableCalendarSync: boolean;
    maintenanceMode: boolean;
  };

  // System Messages
  messages: {
    maintenanceMessage: string;
    announcementBanners: AnnouncementBanner[]; // Array of announcement objects with targeting
    announcementDismissible: boolean;
  };

  // Metadata
  lastUpdatedAt: string;
  lastUpdatedBy: string;
}

// Default configuration
export const DEFAULT_CONFIG: SystemConfig = {
  aiCredits: {
    freeMonthlyLimit: 1000,
    proMonthlyLimit: 1000,
  },
  storage: {
    freeTotalMB: 200,
    proTotalMB: 3072,
    premiumTotalMB: 3072,
    freePhotoSizeMB: 5,
    proPhotoSizeMB: 25,
    premiumPhotoSizeMB: 50,
    freeDocumentSizeMB: 10,
    proDocumentSizeMB: 100,
    premiumDocumentSizeMB: 200,
  },
  itemLimits: {
    free: {
      maxAddresses: 5,
      maxVehicles: 5,
      maxHealthcare: 5,
      maxInsurance: 5,
      maxFinancial: 5,
    },
    pro: {
      maxAddresses: 999999,
      maxVehicles: 999999,
      maxHealthcare: 999999,
      maxInsurance: 999999,
      maxFinancial: 999999,
    },
    premium: {
      maxAddresses: 999999,
      maxVehicles: 999999,
      maxHealthcare: 999999,
      maxInsurance: 999999,
      maxFinancial: 999999,
    },
  },
  familyLimits: {
    free: {
      maxMembers: 3,
      maxCoPilots: 1,
      maxPassengers: 1,
    },
    pro: {
      maxMembers: 999999,
      maxCoPilots: 999999,
      maxPassengers: 999999,
    },
    premium: {
      maxMembers: 999999,
      maxCoPilots: 999999,
      maxPassengers: 999999,
    },
  },
  attachmentLimits: {
    free: {
      maxAttachmentsPerItem: 5,
    },
    pro: {
      maxAttachmentsPerItem: 20,
    },
    premium: {
      maxAttachmentsPerItem: 20,
    },
  },
  shareBundle: {
    expiryDays: 7,
  },
  features: {
    enableCalendarSync: true,
    maintenanceMode: false,
  },
  messages: {
    maintenanceMessage: 'LucidDock is currently under maintenance. We\'ll be back shortly.',
    announcementBanners: [],
    announcementDismissible: true,
  },
  lastUpdatedAt: new Date().toISOString(),
  lastUpdatedBy: '',
};

class AdminConfigService {
  private configDoc = doc(db, 'adminConfig', 'settings');

  /**
   * Deep merge Firestore config with defaults to ensure all fields exist
   */
  private mergeWithDefaults(firestoreConfig: Partial<SystemConfig>): SystemConfig {
    return {
      aiCredits: {
        ...DEFAULT_CONFIG.aiCredits,
        ...firestoreConfig.aiCredits,
      },
      storage: {
        ...DEFAULT_CONFIG.storage,
        ...firestoreConfig.storage,
      },
      itemLimits: {
        free: { ...DEFAULT_CONFIG.itemLimits.free, ...firestoreConfig.itemLimits?.free },
        pro: { ...DEFAULT_CONFIG.itemLimits.pro, ...firestoreConfig.itemLimits?.pro },
        premium: { ...DEFAULT_CONFIG.itemLimits.premium, ...firestoreConfig.itemLimits?.premium },
      },
      familyLimits: {
        free: { ...DEFAULT_CONFIG.familyLimits.free, ...firestoreConfig.familyLimits?.free },
        pro: { ...DEFAULT_CONFIG.familyLimits.pro, ...firestoreConfig.familyLimits?.pro },
        premium: { ...DEFAULT_CONFIG.familyLimits.premium, ...firestoreConfig.familyLimits?.premium },
      },
      attachmentLimits: {
        free: { ...DEFAULT_CONFIG.attachmentLimits.free, ...firestoreConfig.attachmentLimits?.free },
        pro: { ...DEFAULT_CONFIG.attachmentLimits.pro, ...firestoreConfig.attachmentLimits?.pro },
        premium: { ...DEFAULT_CONFIG.attachmentLimits.premium, ...firestoreConfig.attachmentLimits?.premium },
      },
      shareBundle: {
        ...DEFAULT_CONFIG.shareBundle,
        ...firestoreConfig.shareBundle,
      },
      features: {
        enableCalendarSync: firestoreConfig.features?.enableCalendarSync ?? DEFAULT_CONFIG.features.enableCalendarSync,
        maintenanceMode: firestoreConfig.features?.maintenanceMode ?? DEFAULT_CONFIG.features.maintenanceMode,
      },
      messages: {
        ...DEFAULT_CONFIG.messages,
        ...firestoreConfig.messages,
      },
      lastUpdatedAt: firestoreConfig.lastUpdatedAt || DEFAULT_CONFIG.lastUpdatedAt,
      lastUpdatedBy: firestoreConfig.lastUpdatedBy || DEFAULT_CONFIG.lastUpdatedBy,
    };
  }

  /**
   * Get system configuration
   * Returns default config if none exists or if user doesn't have permission
   */
  async getConfig(): Promise<SystemConfig> {
    try {
      const snapshot = await getDoc(this.configDoc);
      if (snapshot.exists()) {
        // Merge with defaults to ensure all fields exist
        return this.mergeWithDefaults(snapshot.data() as Partial<SystemConfig>);
      }
      return DEFAULT_CONFIG;
    } catch (error) {
      // Silently return defaults for permission errors (non-admin users)
      // This is expected behavior - only admins can read the config
      return DEFAULT_CONFIG;
    }
  }

  /**
   * Subscribe to real-time config updates
   * Returns unsubscribe function
   * Note: Silently falls back to defaults for non-admin users (permission errors are expected)
   */
  subscribeToConfig(callback: (config: SystemConfig) => void): () => void {
    return onSnapshot(
      this.configDoc,
      (snapshot) => {
        if (snapshot.exists()) {
          // Merge with defaults to ensure all fields exist
          callback(this.mergeWithDefaults(snapshot.data() as Partial<SystemConfig>));
        } else {
          callback(DEFAULT_CONFIG);
        }
      },
      () => {
        // Silently return defaults for permission errors (non-admin users)
        // This is expected behavior - only admins can read the config
        callback(DEFAULT_CONFIG);
      }
    );
  }

  /**
   * Update system configuration
   * @param config - Partial config to update
   * @param adminEmail - Email of admin making the change
   */
  async updateConfig(
    config: Partial<SystemConfig>,
    adminEmail: string
  ): Promise<void> {
    try {
      const updateData: SystemConfig = {
        ...DEFAULT_CONFIG,
        ...config,
        lastUpdatedAt: new Date().toISOString(),
        lastUpdatedBy: adminEmail,
      };

      // Use setDoc to write the full document (avoids updateDoc shallow-merge quirks)
      await setDoc(this.configDoc, updateData);

      // Verify the write persisted by reading back from server
      const verification = await getDoc(this.configDoc);
      if (!verification.exists()) {
        throw new Error('Config document missing after save');
      }
    } catch (error) {
      console.error('Error updating admin config:', error);
      throw error;
    }
  }

  /**
   * Reset configuration to defaults
   * @param adminEmail - Email of admin making the change
   */
  async resetToDefaults(adminEmail: string): Promise<void> {
    try {
      await setDoc(this.configDoc, {
        ...DEFAULT_CONFIG,
        lastUpdatedAt: new Date().toISOString(),
        lastUpdatedBy: adminEmail,
      });
    } catch (error) {
      console.error('Error resetting admin config:', error);
      throw error;
    }
  }
}

export const adminConfigService = new AdminConfigService();
